// ─────────────────────────────────────────────────────────────────────────────
// Automation Insights — Time Period Helpers
// Direct behavior port of lib/time-periods.ts from the standalone repo.
// Same ISO-week/month math, unchanged — only relocated.
// ─────────────────────────────────────────────────────────────────────────────

import { format, getISOWeek, getISOWeekYear, startOfISOWeek, startOfMonth, subMonths, subWeeks } from 'date-fns';

export const TIME_FILTER_CONFIG = {
  weekYears: 2,
  monthYears: 2,
} as const;

export interface TimePeriodItem {
  value: string;
  label: string;
  year: number;
}

export interface TimePeriodGroup {
  year: number;
  items: TimePeriodItem[];
}

function toWeekValue(year: number, week: number): string {
  return `${year}-W${String(week).padStart(2, '0')}`;
}

export function getRecentWeekGroups(weekYears: number = TIME_FILTER_CONFIG.weekYears): TimePeriodGroup[] {
  const totalWeeks = Math.max(weekYears, 1) * 53;
  const start = startOfISOWeek(new Date());
  const groups = new Map<number, TimePeriodItem[]>();

  for (let index = 0; index < totalWeeks; index += 1) {
    const date = subWeeks(start, index);
    const weekYear = getISOWeekYear(date);
    const isoWeek = getISOWeek(date);

    const item: TimePeriodItem = {
      value: toWeekValue(weekYear, isoWeek),
      label: `Week ${String(isoWeek).padStart(2, '0')}`,
      year: weekYear,
    };

    const existing = groups.get(weekYear) ?? [];
    existing.push(item);
    groups.set(weekYear, existing);
  }

  return Array.from(groups.entries())
    .sort((a, b) => b[0] - a[0])
    .map(([year, items]) => ({ year, items }));
}

export function getRecentMonthGroups(monthYears: number = TIME_FILTER_CONFIG.monthYears): TimePeriodGroup[] {
  const totalMonths = Math.max(monthYears, 1) * 12;
  const start = startOfMonth(new Date());
  const groups = new Map<number, TimePeriodItem[]>();

  for (let index = 0; index < totalMonths; index += 1) {
    const date = subMonths(start, index);
    const year = Number(format(date, 'yyyy'));
    const monthNum = format(date, 'MM');

    const item: TimePeriodItem = {
      value: `${year}-${monthNum}`,
      label: format(date, 'MMMM'),
      year,
    };

    const existing = groups.get(year) ?? [];
    existing.push(item);
    groups.set(year, existing);
  }

  return Array.from(groups.entries())
    .sort((a, b) => b[0] - a[0])
    .map(([year, items]) => ({ year, items }));
}
